import React from "react";
import { motion } from "framer-motion";
import { Star, Coins } from "lucide-react";
import SpeakerButton from "./SpeakerButton";

const STAGES = [
  { name: "Cadet", min: 0 },
  { name: "Pilot", min: 100 },
  { name: "Commander", min: 300 },
  { name: "Navigator", min: 600 },
];

export const QuestTracker = ({ cadet }) => {
  const xp = cadet?.xp || 0;
  const coins = cadet?.coins || 0;
  const nextIdx = STAGES.findIndex(s => xp < s.min);
  const currentIdx = nextIdx === -1 ? STAGES.length - 1 : Math.max(0, nextIdx - 1);
  const next = STAGES[Math.min(currentIdx + 1, STAGES.length - 1)];
  const span = next.min - STAGES[currentIdx].min || 1;
  const pct = nextIdx === -1 ? 100 : Math.min(100, Math.round(((xp - STAGES[currentIdx].min) / span) * 100));

  return (
    <div className="eigoo-glass rounded-3xl p-6 md:p-8" data-testid="quest-tracker">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center eigoo-glow-gold">
            <Star className="text-black" size={26} fill="currentColor" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-yellow-300/80 font-display">Quest Status</div>
            <div className="font-display text-2xl md:text-3xl font-black">
              {cadet?.rank || "Cadet"} <span className="text-white/40 text-base font-bold">→ {next.name}</span>
            </div>
          </div>
          <SpeakerButton
            text={`You are a ${cadet?.rank || "Cadet"}. Next rank: ${next.name}. You have ${xp} experience points and ${coins} Academy Coins. Keep going, Commander!`}
            testid="speaker-quest-status"
            size="sm"
          />
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-400/10 border border-yellow-400/30" data-testid="coins-display">
          <Coins size={20} className="text-yellow-300" />
          <span className="font-display font-bold text-xl text-yellow-200">{coins}</span>
          <span className="text-xs text-yellow-300/70 uppercase tracking-wider">Academy Coins</span>
        </div>
      </div>

      <div className="relative h-6 rounded-full bg-black/60 border border-yellow-300/15 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="h-full bg-gradient-to-r from-yellow-500 via-yellow-300 to-yellow-500 eigoo-glow-gold"
          data-testid="quest-progress-bar"
        />
        <div className="absolute inset-0 flex items-center justify-between px-3">
          {STAGES.map((s, i) => (
            <div key={s.name} className="flex flex-col items-center" style={{ transform: 'translateY(0)' }}>
              <div className={`h-3 w-3 rounded-full ${xp >= s.min ? 'bg-black border border-yellow-700' : 'bg-yellow-300/20 border border-white/10'}`} />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between mt-2 text-[10px] md:text-xs uppercase tracking-widest text-white/50 font-display">
        {STAGES.map((s) => <span key={s.name} className={xp >= s.min ? "text-yellow-300" : ""}>{s.name}</span>)}
      </div>

      <div className="flex items-center justify-between mt-4 text-sm">
        <div className="text-white/70">
          <span className="text-yellow-200 font-display font-bold text-lg">{xp}</span> XP
        </div>
        <div className="text-white/50">
          {nextIdx === -1 ? "Top rank reached!" : `${Math.max(0, next.min - xp)} XP to ${next.name}`}
        </div>
      </div>
    </div>
  );
};

export default QuestTracker;
